import { Cart } from '@api/models/cart';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import {
  AddProductsToCart,
  AddProductToCart,
  ApplyCoupon,
  ApplyCouponError,
  CalculateVat,
  CartActionSuccess,
  ClearWarnings,
  DeleteCart,
  DeleteCartLine,
  DetachCoupon,
  LoadCart,
  ResetCouponError,
  UpdateCartLineQuantity
} from './cart.actions';
import { CartService, CouponsService } from '@api/services';
import { cartIdKey, WebStorageService } from '../../modules/shared/services/web-storage/web-storage-service';
import { map } from 'rxjs';
import { Response } from '@api/models/response';

export class CartStateModel {
  cart: Cart;
  showCart: boolean;
  couponError: string;
  warnings: string[];
  errors: string[];
}

@State<CartStateModel>({
  name: 'cart',
  defaults: {
    cart: {},
    showCart: false,
    couponError: null,
    warnings: [],
    errors: []
  }
})
@Injectable()
export class CartState {
  private cartService = inject(CartService);
  private store = inject(Store);
  private webStorageService = inject(WebStorageService);
  private couponService = inject(CouponsService);

  @Selector()
  static selectCart(state: CartStateModel): Cart {
    return state.cart;
  }

  @Selector()
  static selectCouponError(state: CartStateModel): string {
    return state.couponError;
  }

  @Selector()
  static selectShowCart(state: CartStateModel): boolean {
    return state.showCart;
  }

  @Selector()
  static selectWarnings(state: CartStateModel): string[] {
    return state.warnings;
  }

  @Selector()
  static selectErrors(state: CartStateModel): string[] {
    return state.errors;
  }

  @Selector()
  static selectCountProductsInCart(state: CartStateModel): number {
    if (state.cart) {
      if (state.cart.cartLines === undefined) {
        return 0;
      }
      return state.cart.cartLines.length;
    }
    return 0;
  }

  @Action(LoadCart)
  private load(ctx: StateContext<CartStateModel>, action: LoadCart) {
    return this.cartService
      .apiCartGet({ waitUntilCommitPositionReached: action.commitPosition })
      .subscribe((response: Cart) => {
        if (response && response.id) {
          this.webStorageService.setString(cartIdKey, response.id);
        }
        if (response != null) {
          return ctx.patchState({ cart: response });
        }
      });
  }

  @Action(AddProductToCart)
  private addProductToCart(ctx: StateContext<CartStateModel>, action: AddProductToCart) {
    return this.cartService
      .apiCartCartAddproductPost({ body: action.payload })
      .pipe(map((response: Response) => this.store.dispatch(new CartActionSuccess(response))));
  }

  @Action(AddProductsToCart)
  private addProductsToCart(ctx: StateContext<CartStateModel>, action: AddProductsToCart) {
    return this.cartService
      .apiCartCartAddproductsPost({ body: action.payloads })
      .pipe(map((response: Response) => this.store.dispatch(new CartActionSuccess(response))));
  }

  @Action(UpdateCartLineQuantity)
  private updateCartLineQuantity(ctx: StateContext<CartStateModel>, action: UpdateCartLineQuantity) {
    return this.cartService
      .apiCartCartUpdatequantityPut({ body: action.payload })
      .subscribe((response: Response) => this.store.dispatch(new CartActionSuccess(response)));
  }

  @Action(CartActionSuccess)
  private cartActionSuccess(ctx: StateContext<CartStateModel>, action: CartActionSuccess) {
    if (!action.payload.hasError && action.payload.aggregateGuid) {
      this.webStorageService.setString(cartIdKey, action.payload.aggregateGuid);
    }
    if (action.payload.hasError) {
      ctx.patchState({ errors: action.payload.errors });
    }
    if (action.payload.hasWarning) {
      ctx.patchState({ warnings: action.payload.warnings });
    }

    return this.store.dispatch(new LoadCart(action.payload.commitPosition));
  }

  @Action(DeleteCartLine)
  private deleteCartLine(ctx: StateContext<CartStateModel>, action: DeleteCartLine) {
    return this.cartService
      .apiCartCartLinesCartLineIdDelete({ cartLineId: action.payload.id })
      .subscribe((response: Response) => this.store.dispatch(new CartActionSuccess(response)));
  }

  @Action(ApplyCoupon)
  private applyCoupon(ctx: StateContext<CartStateModel>, action: ApplyCoupon) {
    return this.couponService.apiCouponsApplyPut({ body: { value: action.payload } }).pipe(
      map((response: Response) => {
        if (response.hasError) {
          return this.store.dispatch(new ApplyCouponError(response.errors[0]));
        }

        ctx.patchState({ couponError: null });
        return this.store.dispatch(new CartActionSuccess(response));
      })
    );
  }

  @Action(ApplyCouponError)
  private applyCouponError(ctx: StateContext<CartStateModel>, action: ApplyCouponError) {
    return ctx.patchState({ couponError: action.payload });
  }

  @Action(DetachCoupon)
  private detachCoupon(ctx: StateContext<CartStateModel>, action: DetachCoupon) {
    return this.couponService
      .apiCouponsDetachPut({ body: { value: action.payload } })
      .pipe(map((response: Response) => this.store.dispatch(new CartActionSuccess(response))));
  }

  @Action(ResetCouponError)
  private resetCouponError(ctx: StateContext<CartStateModel>) {
    return ctx.patchState({ couponError: null });
  }

  @Action(DeleteCart)
  private deleteCart({ patchState }: StateContext<CartStateModel>) {
    return patchState({ cart: {} });
  }

  @Action(CalculateVat)
  private calculateVat(ctx: StateContext<CartStateModel>, action: CalculateVat) {
    return this.cartService
      .apiCartCartCalculatevatPost({ body: action.payload })
      .pipe(map((response: Response) => this.store.dispatch(new CartActionSuccess(response))));
  }

  @Action(ClearWarnings)
  private clearWarnings(ctx: StateContext<CartStateModel>) {
    return ctx.patchState({ warnings: [] });
  }
}
